<template>
  <div class="w-full p-6">
    <h1 class="text-2xl font-bold mb-2">Forecasting for PCO</h1>
    <p class="text-muted-foreground mb-6">Analyze and manage financial forecasts for the Planning and Controlling Office</p>
    
    <div class="bg-card rounded-lg shadow p-6">
      <div class="mb-6">
        <h2 class="text-xl font-semibold mb-4">PCO Forecasting Dashboard</h2>
        <p class="text-muted-foreground mb-4">
          Welcome to the PCO Forecasting dashboard. This specialized view is only accessible to users with the Head of PCO business role.
        </p>

        <div class="bg-accent/20 p-4 rounded-md mb-6">
          <p class="font-medium">Note: This is a placeholder component. The actual forecasting functionality will be implemented in future updates.</p>
        </div>
        
        <div class="mb-6 flex space-x-4">
          <button
            @click="openSpreadsheetReviewModal"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Review Excel
          </button>
          <button
            @click="openEmailReviewModal"
            class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Review Email
          </button>
        </div>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-background rounded-md p-4 border border-border">
          <h3 class="font-medium mb-2">Monthly Forecast</h3>
          <div class="h-40 bg-primary/10 rounded flex items-center justify-center">
            <span class="text-muted-foreground">Chart placeholder</span>
          </div>
        </div>
        
        <div class="bg-background rounded-md p-4 border border-border">
          <h3 class="font-medium mb-2">Quarterly Forecast</h3>
          <div class="h-40 bg-primary/10 rounded flex items-center justify-center">
            <span class="text-muted-foreground">Chart placeholder</span>
          </div>
        </div>
        
        <div class="bg-background rounded-md p-4 border border-border md:col-span-2">
          <h3 class="font-medium mb-2">Annual Forecast Comparison</h3>
          <div class="h-40 bg-primary/10 rounded flex items-center justify-center">
            <span class="text-muted-foreground">Chart placeholder</span>
          </div>
        </div>
      </div>
      
      <!-- Process Status Indicator -->
      <div class="mt-4" v-if="processStatus">
        <div class="flex items-center space-x-2">
          <div
            class="w-3 h-3 rounded-full"
            :class="{
              'bg-blue-500 animate-pulse': processStatus === 'active',
              'bg-green-500': processStatus === 'completed',
              'bg-red-500': processStatus === 'error'
            }"
          ></div>
          <span
            class="text-sm"
            :class="{
              'text-blue-700': processStatus === 'active',
              'text-green-700': processStatus === 'completed',
              'text-red-700': processStatus === 'error'
            }"
          >
            {{
              processStatus === 'active' ? 'Processing forecast data...' :
              processStatus === 'completed' ? 'Forecast process completed' :
              'Error in forecast process'
            }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Email Review Modal -->
    <EmailReviewModal
      :is-open="showEmailReviewModal"
      :process-step-type="currentProcessStepType"
      :recipient-email="recipientEmail"
      :template-variables="templateVariables"
      @close="closeEmailReviewModal"
      @send-success="handleEmailSent"
    />
    
    <!-- Spreadsheet Review Modal -->
    <SpreadsheetReviewModal
      :is-open="showSpreadsheetReviewModal"
      @close="closeSpreadsheetReviewModal"
      @save="handleSpreadsheetSaved"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import EmailReviewModal from './EmailReviewModal.vue';
import SpreadsheetReviewModal from './SpreadsheetReviewModal.vue';

export default defineComponent({
  name: 'ForecastingPcoView',
  components: {
    EmailReviewModal,
    SpreadsheetReviewModal
  },
  setup() {
    const showEmailReviewModal = ref(false);
    const showSpreadsheetReviewModal = ref(false);
    const currentProcessStepType = ref('DOWNLOAD'); // Use the DOWNLOAD process step type that has a mapping
    const recipientEmail = ref('luka.novak@company.com');
    const processStatus = ref(null); // Add processStatus ref to fix TypeScript errors
    
    // Define template variables, TODO: should be given when calling this method
    const templateVariables = ref({
      forecastPeriod: 'FCQ1',
      firstName: 'Luka',
      lastName: 'Novak',
      forecastData: 'Test-Data 123',
      companyName: 'Fraport'
    });
    
    // Email Modal functions
    const openEmailReviewModal = () => {
      showEmailReviewModal.value = true;
    };
    
    const closeEmailReviewModal = () => {
      showEmailReviewModal.value = false;
    };
    
    const handleEmailSent = () => {
      // Here you would update the UI or state to reflect that the email was sent
      // For example, you might want to refresh data or show a notification
      console.log('Email was sent successfully');
      
      // You could also update the process step to the next step in the workflow
      currentProcessStepType.value = 'Email Sent'; // Move to the next step
    };
    
    // Spreadsheet Modal functions
    const openSpreadsheetReviewModal = () => {
      showSpreadsheetReviewModal.value = true;
    };
    
    const closeSpreadsheetReviewModal = () => {
      showSpreadsheetReviewModal.value = false;
    };
    
    const handleSpreadsheetSaved = () => {
      console.log('Spreadsheet data was saved successfully');
      // Here you would handle any post-save actions
    };
    
    return {
      // Email Modal
      showEmailReviewModal,
      currentProcessStepType,
      recipientEmail,
      templateVariables,
      openEmailReviewModal,
      closeEmailReviewModal,
      handleEmailSent,
      
      // Spreadsheet Modal
      showSpreadsheetReviewModal,
      openSpreadsheetReviewModal,
      closeSpreadsheetReviewModal,
      handleSpreadsheetSaved,
      
      // Process Status
      processStatus
    };
  }
});
</script>
